import {gql} from '@apollo/client';

import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {StandardFeatureItemState} from 'auth/capabilities/getCapabilityFeatures';
import {
  DisabledReason,
  FeatureStatus,
  getFeatureStatus,
  makeFeatureStatus,
} from 'auth/feature-enablement/FeatureStatus';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  BioentityOpenStateQuery,
  BioentityOpenStateQueryVariables,
} from 'molbio/__generated/BioentityOpenStateQuery';
import * as RegistryUtil from 'registry/RegistryUtil';

const BIOENTITY_OPEN_STATE_QUERY = gql`
  query BioentityOpenStateQuery($ids: [StrictString]!) @skipAudit @engComponent(name: MOLBIO) {
    bioentities(ids: $ids) {
      ... on RegistryItemInterface {
        id
        folder {
          id
          source {
            sourceType
          }
        }
        isBiologicsOwned
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const INACCESSIBLE_BIOENTITY_OPEN_STATUS = makeFeatureStatus(
  false,
  DisabledReason.INSUFFICIENT_PERMISSION,
  "You don't have permissions to view this item."
);

export interface BioentityOpenFeatureStatus {
  featureStatus: FeatureStatus;
  itemState?: StandardFeatureItemState;
}

export const ENABLED_BIOENTITY_OPEN_FEATURE_STATUS: BioentityOpenFeatureStatus = {
  featureStatus: {enabled: true},
};

export const UNAVAILABLE_BIOENTITY_OPEN_FEATURE_STATUS: BioentityOpenFeatureStatus = {
  featureStatus: makeFeatureStatus(
    false,
    DisabledReason.INSUFFICIENT_PERMISSION,
    'Unable to verify permissions to open this item.'
  ),
};

export function getOpenFeatureStatusForPartId(
  partId: string,
  openFeatureStatusesById: Record<string, BioentityOpenFeatureStatus>,
  missingStatus: BioentityOpenFeatureStatus = UNAVAILABLE_BIOENTITY_OPEN_FEATURE_STATUS
): BioentityOpenFeatureStatus {
  return openFeatureStatusesById[partId] ?? missingStatus;
}

function getBioentityOpenFeatureStatusFromBioentity(
  id: string,
  bioentity: BioentityOpenStateQuery['bioentities'][number] | undefined
): BioentityOpenFeatureStatus {
  if (bioentity == null || isInaccessible(bioentity)) {
    return {featureStatus: INACCESSIBLE_BIOENTITY_OPEN_STATUS};
  }
  const itemState = {
    isRegistered: RegistryUtil.isRegistered({
      folder: {source: bioentity.folder.source ?? undefined},
    }),
    isBiologicsOwned: bioentity.isBiologicsOwned,
  };
  return {
    featureStatus: getFeatureStatus({
      feature: StandardFeature.OPEN,
      item: {id},
      itemState,
    }),
    itemState,
  };
}

export async function getBioentityOpenFeatureStatusesById(
  ids: Array<string>
): Promise<Record<string, BioentityOpenFeatureStatus>> {
  const uniqueIds = Array.from(new Set(ids));
  if (uniqueIds.length === 0) {
    return {};
  }
  const data = await doQuerySafe<BioentityOpenStateQuery, BioentityOpenStateQueryVariables>(
    {query: BIOENTITY_OPEN_STATE_QUERY},
    {variables: {ids: uniqueIds}}
  );
  const statusesById = Object.fromEntries(
    data.bioentities.map((bioentity) => {
      const id = getAccessibleOrInaccessibleId(bioentity);
      return [id, getBioentityOpenFeatureStatusFromBioentity(id, bioentity)];
    })
  );
  return Object.fromEntries(
    uniqueIds.map((id) => [id, statusesById[id] ?? {featureStatus: INACCESSIBLE_BIOENTITY_OPEN_STATUS}])
  );
}

export async function getBioentityOpenFeatureStatus(id: string): Promise<FeatureStatus> {
  return (
    (await getBioentityOpenFeatureStatusesById([id]))[id]?.featureStatus ?? INACCESSIBLE_BIOENTITY_OPEN_STATUS
  );
}

export async function openBioentityWithFeatureCheck(id: string): Promise<void> {
  const result = (await getBioentityOpenFeatureStatusesById([id]))[id];
  if (!result?.featureStatus.enabled || result.itemState == null) {
    return;
  }
  FileBrowserActions.openFile(id, {itemState: result.itemState});
}
